import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, normalizeClass as _normalizeClass, createBlock as _createBlock, unref as _unref, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-2 py-2",
  "data-testid": "ds-attendance-card-activity"
}
const _hoisted_2 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_3 = { class: "text-[#52526F] text-xs font-bold leading-[14px] tracking-[0.06px]" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h4", { class: "text-textPrimary text-md font-bold" }, "Registro de Atividade · Jornada", -1)
const _hoisted_5 = { class: "text-sm text-textPrimary flex flex-col gap-0.5" }
const _hoisted_6 = {
  key: 0,
  class: "flex flex-wrap gap-2"
}
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_9 = { class: "ml-auto" }
const _hoisted_10 = {
  key: 1,
  class: "flex flex-wrap gap-y-3 gap-x-4 sm:gap-x-0 items-center"
}
const _hoisted_11 = ["title"]
const _hoisted_12 = { class: "text-[#52526F] text-xs font-bold truncate leading-[14px] tracking-[0.06px]" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("span", { class: "text-textPrimary text-md mb-1 font-bold truncate" }, "Data do plantão", -1)
const _hoisted_14 = { class: "truncate" }
const _hoisted_15 = {
  key: 0,
  class: "flex flex-wrap gap-1 mt-1"
}
const _hoisted_16 = ["title"]
const _hoisted_17 = /*#__PURE__*/_createElementVNode("span", { class: "text-textPrimary text-md mb-1 font-bold truncate" }, "Data upload", -1)
const _hoisted_18 = { class: "truncate" }
const _hoisted_19 = ["title"]
const _hoisted_20 = /*#__PURE__*/_createElementVNode("span", { class: "text-textPrimary font-bold text-md mb-1 truncate" }, "Escala", -1)
const _hoisted_21 = { class: "truncate" }
const _hoisted_22 = { class: "text-textPrimary truncate block" }
const _hoisted_23 = ["title"]
const _hoisted_24 = { class: "text-textPrimary text-md mb-1 font-bold flex items-center gap-2" }
const _hoisted_25 = { class: "relative inline-flex items-center" }
const _hoisted_26 = /*#__PURE__*/_createElementVNode("i", { class: "pi pi-briefcase text-primary" }, null, -1)
const _hoisted_27 = { class: "truncate" }
const _hoisted_28 = ["title"]
const _hoisted_29 = /*#__PURE__*/_createElementVNode("span", { class: "text-textPrimary font-bold text-md mb-1 truncate" }, "Status", -1)
const _hoisted_30 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_31 = { class: "basis-full sm:basis-auto sm:ml-auto shrink-0 flex justify-end" }

import { computed } from 'vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  ACTIVITY_RISK_FLAG_LABELS,
  ACTIVITY_TYPE_LABELS,
  ActivityType,
  ContainerKind,
  CoveredLegs,
  DSAttendanceStatus,
  DS_ATTENDANCE_STATUS_LABELS,
  EFFECTIVE_SOURCE_KIND_LABELS,
  EffectiveSourceKind,
  type ActivityRiskFlag
} from '@/constants/ds-attendance.constants'
import type { DSAttendanceEffectiveSummary } from '@/types/ds-attendance.types'
import { formatDateInOffset } from '@/utils/date'
import { toHHmm } from '../utils/activity-time'
import {
  NO_SCHEDULE_EXPECTED_LABEL,
  buildNoScheduleExpectedTooltip
} from '../utils/prefill-empty-state'
import {
  APPROVAL_MODE_LABELS,
  DSAttendanceApprovalMode,
  manualReviewReasonLabel
} from '@/constants/ds-attendance-auto-approval.constants'

interface ActivityLeg {
  id: 'checkin' | 'checkout'
  label: string
  value: string
  state: string
  pending: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DsAttendanceCard',
  props: {
  attendanceId: {
    type: [Number, String],
    required: true
  },
  date: {
    type: String,
    default: ''
  },
  submissionDate: {
    type: String,
    default: ''
  },
  unit: {
    type: String,
    default: ''
  },
  schedule: {
    type: String,
    default: ''
  },
  shifts: {
    type: String,
    default: ''
  },
  shiftsCount: { type: Number, default: 0 },
  /** Pacote 2026-09-10 — pílulas "Antecipe" / "À vista" (backend #2750). */
  hasAntecipe: { type: Boolean, default: false },
  hasInCash: { type: Boolean, default: false },
  hasDivergentEntries: { type: Boolean, default: false },
  stationChangedAfterAudit: { type: Boolean, default: false },
  /**
   * Fase 1 do fluxo de auditoria — tri-estado de propósito: `undefined` é backend anterior
   * à fase 1 (nada a dizer) e só `false` significa "a escala não prevê plantão aqui".
   * O `default: undefined` desliga o cast de Boolean ausente → `false` do Vue.
   */
  hasScheduleExpected: { type: Boolean, default: undefined },
  /**
   * Fase 2 do fluxo de auditoria (spec 2026-09-11, §6) — motivo da revisão manual e modo de
   * aprovação. `null`/ausente em backend anterior à fase 2: nenhum chip aparece.
   */
  manualReviewReason: { type: String as () => string | null, default: null },
  approvalMode: { type: String as () => string | null, default: null },
  /**
   * F2/M3 — espécie do container. Ausente/`FOLHA` mantém o card de hoje, byte a byte;
   * `ATIVIDADE` troca o corpo do card pela variante de Registro de Atividade.
   */
  kind: { type: String as () => string | null, default: null },
  activityType: { type: String as () => string | null, default: null },
  /** Rótulo vindo do backend; só usado quando o código não está no catálogo local. */
  activityTypeLabel: { type: String as () => string | null, default: null },
  activityDescription: { type: String as () => string | null, default: null },
  coveredLegs: { type: String as () => string | null, default: null },
  declaredCheckin: { type: String as () => string | null, default: null },
  declaredCheckout: { type: String as () => string | null, default: null },
  /** Fuso da escala do plantão (`-03:00`). Ausente, o card assume Brasília. */
  timezone: { type: String as () => string | null, default: null },
  doctorName: { type: String as () => string | null, default: null },
  doctorCrm: { type: String as () => string | null, default: null },
  attachmentCount: { type: Number as () => number | null, default: null },
  submittedByName: { type: String as () => string | null, default: null },
  /** Chaves `true` viram chips explicativos. O SCORE numérico nunca é exibido (§13). */
  activityRiskFlags: {
    type: Object as () => Partial<Record<ActivityRiskFlag, boolean>> | null,
    default: null
  },
  effectiveSummary: {
    type: Object as () => DSAttendanceEffectiveSummary | null,
    default: null
  },
  afterClosure: { type: Boolean, default: false },
  legacyMigrated: { type: Boolean, default: false },
  status: {
    type: String as () => DSAttendanceStatus,
    required: true,
    validator: (val: DSAttendanceStatus) => Object.values(DSAttendanceStatus).includes(val)
  }
},
  emits: ["details"],
  setup(__props) {

const props = __props;





const formattedDate = computed(() => {
  if (props.date) {
    try {
      return format(new Date(props.date + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })
    } catch (e) {
      return props.date
    }
  }
  return ''
})

/** Rótulo pt-BR do motivo; código desconhecido aparece cru em vez de virar chip vazio. */
const manualReviewReasonText = computed(() => manualReviewReasonLabel(props.manualReviewReason))

const isAutomaticallyApproved = computed(
  () => props.approvalMode === DSAttendanceApprovalMode.AUTOMATIC
)

/** Tooltip do chip cinza — usa a data já formatada em dd/MM/yyyy que o card exibe. */
const noScheduleExpectedTooltip = computed(() =>
  buildNoScheduleExpectedTooltip({
    scheduleName: props.schedule,
    date: formattedDate.value,
    shifts: props.shifts
  })
)

/* --------------------------------------------------------------------------- *
 * F2/M3 — variante "Registro de Atividade" do card.
 * --------------------------------------------------------------------------- */

const isActivity = computed(() => props.kind === ContainerKind.ATIVIDADE)

/** Catálogo local primeiro; o rótulo do backend só cobre código que ainda não conhecemos. */
const activityTypeText = computed(
  () =>
    ACTIVITY_TYPE_LABELS[props.activityType as ActivityType] ??
    props.activityTypeLabel ??
    props.activityType ??
    'Tipo não informado'
)

const doctorLabel = computed(() => {
  if (!props.doctorName) return ''
  return props.doctorCrm ? `${props.doctorName} (CRM ${props.doctorCrm})` : props.doctorName
})

const attachmentLabel = computed(() => {
  const count = props.attachmentCount
  if (count === null || count === undefined) return 'Anexos não informados'
  return count === 1 ? '1 anexo' : `${count} anexos`
})

const submitterLabel = computed(() => props.submittedByName ?? '')

/**
 * `HH:mm` no fuso da ESCALA — o mesmo `toHHmm` da tela de auditoria (I12/I1). Ler com
 * `new Date` convertia para o fuso do navegador e fazia o card e a auditoria mostrarem
 * horários diferentes para o mesmo registro. Valor não parseável volta cru em vez de sumir.
 */
function formatHour(value: string | null): string {
  if (!value) return ''
  return toHHmm(value, props.timezone ?? undefined) || value
}

function sourceLabel(source: string | null | undefined): string {
  if (!source) return ''
  return EFFECTIVE_SOURCE_KIND_LABELS[source as EffectiveSourceKind] ?? source
}

function coversLeg(leg: 'checkin' | 'checkout'): boolean {
  if (props.coveredLegs === CoveredLegs.BOTH) return true
  return props.coveredLegs === (leg === 'checkin' ? CoveredLegs.CHECKIN : CoveredLegs.CHECKOUT)
}

/**
 * Uma linha por perna: a que ESTE registro declara aparece como "a auditar" (ou "aprovado",
 * depois do veredito); a que o sistema já resolveu por outra fonte aparece como "já tem",
 * com o nome da fonte. Perna sem nada a dizer não vira chip vazio.
 */
function buildLeg(id: 'checkin' | 'checkout', label: string): ActivityLeg | null {
  const declared = id === 'checkin' ? props.declaredCheckin : props.declaredCheckout
  const effective =
    id === 'checkin' ? props.effectiveSummary?.checkin : props.effectiveSummary?.checkout
  const approved = props.status === DSAttendanceStatus.APPROVED

  if (coversLeg(id) && declared) {
    return {
      id,
      label,
      value: `declarado ${formatHour(declared)}`,
      state: approved ? 'aprovado' : 'a auditar',
      pending: !approved
    }
  }

  if (effective?.value) {
    const source = sourceLabel(effective.source)
    return {
      id,
      label,
      value: source ? `${formatHour(effective.value)} · ${source}` : formatHour(effective.value),
      state: 'já tem',
      pending: false
    }
  }

  if (coversLeg(id)) {
    return {
      id,
      label,
      value: 'declarado —',
      state: approved ? 'aprovado' : 'a auditar',
      pending: !approved
    }
  }

  return null
}

const activityLegs = computed<ActivityLeg[]>(() =>
  [buildLeg('checkin', 'Check-in'), buildLeg('checkout', 'Check-out')].filter(
    (leg): leg is ActivityLeg => leg !== null
  )
)

/**
 * Só os critérios marcados como `true`, na ordem de declaração do catálogo. O card NÃO
 * mostra o `activityRiskScore`: número sem explicação não é acionável (§13 do spec).
 */
const riskFlagLabels = computed<string[]>(() => {
  const flags = props.activityRiskFlags
  if (!flags) return []
  return (Object.keys(ACTIVITY_RISK_FLAG_LABELS) as ActivityRiskFlag[])
    .filter((key) => flags[key] === true)
    .map((key) => ACTIVITY_RISK_FLAG_LABELS[key])
})

/** Aprovado, mas quem venceu o ranking da perna foi a folha auditada (mockup M3, card 4). */
const supersededBySheet = computed(() => {
  if (props.status !== DSAttendanceStatus.APPROVED) return false
  const summary = props.effectiveSummary
  if (!summary) return false
  return (['checkin', 'checkout'] as const).some(
    (leg) => coversLeg(leg) && summary[leg]?.source === EffectiveSourceKind.SHEET
  )
})

/**
 * Data do upload no fuso da ESCALA, pelo mesmo motivo do `formatHour` acima: com
 * `new Date(iso)` puro um upload de madrugada trocava de dia conforme o fuso da
 * máquina de quem olha, e dois auditores viam datas diferentes para o mesmo
 * registro. Era a única data do card lida sem offset explícito.
 */
const formattedSubmissionDate = computed(() => {
  if (!props.submissionDate) return ''
  return formatDateInOffset(props.submissionDate, props.timezone ?? undefined) ?? ''
})

return (_ctx: any,_cache: any) => {
  const _component_Badge = _resolveComponent("Badge")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["border rounded-lg p-4 py-2 mb-4", __props.hasDivergentEntries ? 'border-negative bg-red-50' : 'border-gray-200']),
    "data-testid": "ds-attendance-card"
  }, [
    (isActivity.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", _hoisted_3, "#" + _toDisplayString(__props.attendanceId), 1),
            _hoisted_4,
            _createVNode(StatusBadge, { status: __props.status }, null, 8, ["status"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("span", null, [
              _createTextVNode(" Tipo: "),
              _createElementVNode("b", null, _toDisplayString(activityTypeText.value), 1),
              (__props.activityDescription)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(" — “" + _toDisplayString(__props.activityDescription) + "”", 1)
                  ], 64))
                : _createCommentVNode("", true),
              (doctorLabel.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(" · "),
                    _createElementVNode("b", null, _toDisplayString(doctorLabel.value), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("span", null, [
              _createTextVNode(" Plantão "),
              _createElementVNode("b", null, _toDisplayString(formattedDate.value), 1),
              _createTextVNode(" · " + _toDisplayString(__props.unit || 'Unidade não informada') + " · " + _toDisplayString(__props.schedule || 'Escala não informada'), 1)
            ]),
            _createElementVNode("span", null, [
              _createElementVNode("b", null, _toDisplayString(attachmentLabel.value), 1),
              (submitterLabel.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(" · enviado por "),
                    _createElementVNode("b", null, _toDisplayString(submitterLabel.value), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ]),
          (activityLegs.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activityLegs.value, (leg) => {
                  return (_openBlock(), _createElementBlock("span", {
                    key: leg.id,
                    "data-testid": `ds-attendance-card-leg-${leg.id}`,
                    class: "inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-textPrimary"
                  }, [
                    _createElementVNode("b", null, _toDisplayString(leg.label), 1),
                    _createTextVNode(" " + _toDisplayString(leg.value) + " ", 1),
                    _createElementVNode("span", {
                      class: _normalizeClass([
              'rounded-full px-2 py-0.5 text-[10px] font-bold',
              leg.pending ? 'bg-orange-50 text-alert' : 'bg-green-50 text-sucess'
            ])
                    }, _toDisplayString(leg.state), 3)
                  ], 8, _hoisted_7))
                }), 128))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(riskFlagLabels.value, (flag) => {
              return (_openBlock(), _createBlock(LabelBadge, {
                key: flag,
                "data-testid": "ds-attendance-card-risk-chip",
                label: flag,
                color: "orange",
                size: "small",
                title: "Critério que elevou o risco deste registro"
              }, null, 8, ["label"]))
            }), 128)),
            (__props.afterClosure)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 0,
                  "data-testid": "ds-attendance-card-after-closure",
                  label: "Recebido após fechamento",
                  color: "orange",
                  size: "small",
                  title: "O período do plantão já estava fechado quando este registro chegou"
                }))
              : _createCommentVNode("", true),
            (__props.legacyMigrated)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 1,
                  "data-testid": "ds-attendance-card-legacy-migrated",
                  label: "Migrado da Avaliação de Evidências",
                  color: "gray",
                  size: "small",
                  title: "Linha nascida da migração retroativa da fila antiga"
                }))
              : _createCommentVNode("", true),
            (supersededBySheet.value)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 2,
                  "data-testid": "ds-attendance-card-superseded",
                  label: "Superado pela folha",
                  color: "gray",
                  size: "small",
                  title: "O horário efetivo do plantão veio da folha auditada, não deste registro"
                }))
              : _createCommentVNode("", true),
            (__props.hasAntecipe)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 3,
                  "data-testid": "ds-attendance-card-antecipe",
                  label: "Antecipe",
                  color: "orange",
                  size: "small",
                  title: "Algum plantão deste registro tem antecipação (Antecipe) solicitada ou paga"
                }))
              : _createCommentVNode("", true),
            (__props.hasInCash)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 4,
                  "data-testid": "ds-attendance-card-a-vista",
                  label: "À vista",
                  color: "blue",
                  size: "small",
                  title: "Algum plantão deste registro é pago à vista"
                }))
              : _createCommentVNode("", true),
            (__props.stationChangedAfterAudit)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 5,
                  "data-testid": "ds-attendance-card-station-changed",
                  label: "Plantão alterado após auditoria",
                  color: "orange",
                  size: "small",
                  title: "A vaga/plantão foi criada, alterada ou excluída depois do início da auditoria"
                }))
              : _createCommentVNode("", true),
            (manualReviewReasonText.value)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 6,
                  "data-testid": "ds-attendance-card-manual-review-reason",
                  label: manualReviewReasonText.value,
                  color: "orange",
                  size: "small",
                  title: "Motivo pelo qual este registro foi para a fila de revisão manual"
                }, null, 8, ["label"]))
              : _createCommentVNode("", true),
            (isAutomaticallyApproved.value)
              ? (_openBlock(), _createBlock(LabelBadge, {
                  key: 7,
                  "data-testid": "ds-attendance-card-approval-mode",
                  label: _unref(APPROVAL_MODE_LABELS).AUTOMATIC,
                  color: "green",
                  size: "small",
                  title: "Aprovado automaticamente: todos os critérios da política bateram"
                }, null, 8, ["label"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(OutlinedButton, {
                "data-testid": "ds-attendance-card-ver-detalhes-button",
                text: "Ver detalhes",
                width: "130px",
                height: "32px",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('details', props.attendanceId)))
              })
            ])
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createElementVNode("div", {
            class: "basis-[calc(50%-8px)] sm:basis-[140px] shrink-0 min-w-0 flex flex-col text-sm text-start",
            title: `#${__props.attendanceId} - ${formattedDate.value}`
          }, [
            _createElementVNode("span", _hoisted_12, "#" + _toDisplayString(__props.attendanceId), 1),
            _hoisted_13,
            _createElementVNode("span", _hoisted_14, _toDisplayString(formattedDate.value), 1),
            (__props.hasAntecipe || __props.hasInCash)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  (__props.hasAntecipe)
                    ? (_openBlock(), _createBlock(LabelBadge, {
                        key: 0,
                        "data-testid": "ds-attendance-card-antecipe",
                        label: "Antecipe",
                        color: "orange",
                        size: "small",
                        title: "Algum plantão deste registro tem antecipação (Antecipe) solicitada ou paga"
                      }))
                    : _createCommentVNode("", true),
                  (__props.hasInCash)
                    ? (_openBlock(), _createBlock(LabelBadge, {
                        key: 1,
                        "data-testid": "ds-attendance-card-a-vista",
                        label: "À vista",
                        color: "blue",
                        size: "small",
                        title: "Algum plantão deste registro é pago à vista"
                      }))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ], 8, _hoisted_11),
          _createElementVNode("div", {
            class: "basis-[calc(50%-8px)] sm:basis-[130px] shrink-0 min-w-0 flex flex-col text-sm text-start sm:border-l sm:border-gray-200 sm:pl-4",
            title: formattedSubmissionDate.value
          }, [
            _hoisted_17,
            _createElementVNode("span", _hoisted_18, _toDisplayString(formattedSubmissionDate.value), 1)
          ], 8, _hoisted_16),
          _createElementVNode("div", {
            class: "basis-[calc(50%-8px)] sm:basis-[130px] grow shrink-0 min-w-0 flex flex-col text-sm text-start sm:pl-4",
            title: `${__props.schedule || 'Não informada'} - ${__props.unit || 'Não informada'}`
          }, [
            _hoisted_20,
            _createElementVNode("span", _hoisted_21, _toDisplayString(__props.schedule || 'Não informada'), 1),
            _createElementVNode("small", _hoisted_22, _toDisplayString(__props.unit || 'Não informada'), 1)
          ], 8, _hoisted_19),
          _createElementVNode("div", {
            class: "basis-[calc(50%-8px)] sm:basis-[110px] grow shrink-0 min-w-0 flex flex-col text-sm text-start sm:pl-4",
            title: __props.shifts
          }, [
            _createElementVNode("span", _hoisted_24, [
              _createElementVNode("div", _hoisted_25, [
                _hoisted_26,
                (__props.shiftsCount > 1)
                  ? (_openBlock(), _createBlock(_component_Badge, {
                      key: 0,
                      value: __props.shiftsCount,
                      severity: "info",
                      class: "absolute -top-2 -right-2 scale-75"
                    }, null, 8, ["value"]))
                  : _createCommentVNode("", true)
              ]),
              _createTextVNode(" Turno(s) ")
            ]),
            _createElementVNode("span", _hoisted_27, _toDisplayString(__props.shifts), 1)
          ], 8, _hoisted_23),
          _createElementVNode("div", {
            class: "basis-[calc(50%-8px)] sm:basis-[130px] grow shrink-0 min-w-0 flex flex-col text-sm text-start sm:pl-4",
            title: _unref(DS_ATTENDANCE_STATUS_LABELS)[__props.status]
          }, [
            _hoisted_29,
            _createElementVNode("div", _hoisted_30, [
              _createVNode(StatusBadge, { status: __props.status }, null, 8, ["status"]),
              (__props.stationChangedAfterAudit)
                ? (_openBlock(), _createBlock(LabelBadge, {
                    key: 0,
                    "data-testid": "ds-attendance-card-station-changed",
                    label: "Plantão alterado após auditoria",
                    color: "orange",
                    size: "small",
                    title: "A vaga/plantão foi criada, alterada ou excluída depois do início da auditoria"
                  }))
                : _createCommentVNode("", true),
              (__props.hasScheduleExpected === false)
                ? (_openBlock(), _createBlock(LabelBadge, {
                    key: 1,
                    "data-testid": "ds-attendance-card-no-schedule-expected",
                    label: _unref(NO_SCHEDULE_EXPECTED_LABEL),
                    color: "gray",
                    size: "small",
                    title: noScheduleExpectedTooltip.value
                  }, null, 8, ["label", "title"]))
                : _createCommentVNode("", true),
              (manualReviewReasonText.value)
                ? (_openBlock(), _createBlock(LabelBadge, {
                    key: 2,
                    "data-testid": "ds-attendance-card-manual-review-reason",
                    label: manualReviewReasonText.value,
                    color: "orange",
                    size: "small",
                    title: "Motivo pelo qual este registro foi para a fila de revisão manual"
                  }, null, 8, ["label"]))
                : _createCommentVNode("", true),
              (isAutomaticallyApproved.value)
                ? (_openBlock(), _createBlock(LabelBadge, {
                    key: 3,
                    "data-testid": "ds-attendance-card-approval-mode",
                    label: _unref(APPROVAL_MODE_LABELS).AUTOMATIC,
                    color: "green",
                    size: "small",
                    title: "Aprovado automaticamente: todos os critérios da política bateram"
                  }, null, 8, ["label"]))
                : _createCommentVNode("", true)
            ])
          ], 8, _hoisted_28),
          _createElementVNode("div", _hoisted_31, [
            _createVNode(OutlinedButton, {
              "data-testid": "ds-attendance-card-ver-detalhes-button",
              text: "Ver detalhes",
              width: "130px",
              height: "32px",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('details', props.attendanceId)))
            })
          ])
        ]))
  ], 2))
}
}

})